import React, { useState, useEffect, useCallback, useContext, useRef } from 'react'
import {
    IconButton, Menu, MenuItem, Skeleton, Typography, Stack,
    Box, Paper, InputBase, Divider
} from '@mui/material'
import { Close, Refresh } from '@mui/icons-material'
import { VscJson } from "react-icons/vsc"
import { blue } from "@mui/material/colors"
import MenuIcon from '@mui/icons-material/Menu'
import { NodeRendererProps, Tree, TreeApi } from 'react-arborist'
import { v4 as uuidv4 } from 'uuid'

import { post } from '../../../utils/http'
import { ImageData, FileListNodeProp } from '../../../utils/types'
import { ViewerContext } from './context'
import { handleAnnoCheckbox } from './common'


const Node = ({ node, style }: NodeRendererProps<any>) => {
    return (
        <div style={style} className='node-container cursor-pointer' onClick={() => node.toggle()}>
            {node.data.is_loading
                ? <div>
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
                : <input
                    type="checkbox"
                    value=""
                    className="form-check-input m-0 shadow-none toggle_slide_anno"
                    style={{pointerEvents: 'none'}}
                    checked={node.data.checked}
                    readOnly
                />
            }
            {node.data.name.includes('/')
                ? <VscJson style={{pointerEvents: 'none', fontSize: '1.5rem', color: blue[700]}} />
                : null
            }
            <Typography className='node-text' title={node.data.name.split('/').pop()}>
                {node.data.name.split('/').pop()}
            </Typography>
        </div>
    )
}


interface JsonProps {
    imageData: ImageData
    list: string[]
    refreshList: Function
    showJsonTileOverlay: Function
    renderJsonHeatmap: Function
}

const JsonList = ({
    imageData,
    list,
    refreshList,
    showJsonTileOverlay,
    renderJsonHeatmap
}: JsonProps) => {
    const treeRef = useRef<TreeApi<FileListNodeProp>>(null)
    
    const { jsonClasses, jsonName, jsonClassFilter, readonly } = useContext(ViewerContext)

    const [jsonFiles, setJsonFiles] = useState<FileListNodeProp[]>([])
    const [searchKey, setSearchKey] = useState<string>('')
    const [focusNodeId, setFocusNodeId] = useState<string>('')  // eslint-disable-line
    const [selectedJson, setSelectedJson] = useState<string>('')
    const [isListLoading, setIsListLoading] = useState<boolean>(false)
    const [jsonAnchorEl, setJsonAnchorEl] = useState<null | HTMLElement>(null)
    const [boxHeight, setBoxHeight] = useState<number>(0)
    
    const rowHeight = 32
    const maxHeight: number = (window.visualViewport?.height ?? window.innerHeight) - 320
    const jsonMenuOpen = Boolean(jsonAnchorEl)
    const isDev = (localStorage.getItem('role') || '') === 'dev'

    const filterJsonList = useCallback(async () => {
        const jsonList = list.filter((name: string) => (name.length && (name.endsWith('.json') || name.endsWith('.geojson'))))
        const files = jsonList.map(name => {
            let classes: FileListNodeProp[] = []
            if (name in jsonClasses) {
                classes = jsonClasses[name].map(x => ({
                    id: uuidv4(),
                    name: x.name,
                    checked: x.name === jsonClassFilter,
                    children: []
                }))
            }
            return {
                id: uuidv4(),
                name: name,
                checked: jsonName === name,
                children: classes
            }
        })
        setJsonFiles(files)
        setIsListLoading(false)
        setBoxHeight(18 + (files.length * rowHeight))
    }, [list, jsonClasses, jsonName, jsonClassFilter])

    useEffect(() => {
        filterJsonList()
    }, [list])  // eslint-disable-line

    const handleCloseJSONBtn = () => setJsonAnchorEl(null)
    
    const downloadJSONFile = async () => {
        handleCloseJSONBtn()
        try {
            const filename = selectedJson.split('/')[selectedJson.split('/').length - 1] || `${imageData.image_unique_id}.json`
            const payload = { s3_name: selectedJson.split('/')[0], key: selectedJson }
            const fileExist = await post('S3/file-exist', payload)
            const res = await post('S3/getaccess', { s3_name: payload.s3_name, key: fileExist.key })
            const a = document.createElement('a')
            document.body.appendChild(a)
            a.style.display = 'none'
            a.href = res.url
            a.download = filename
            a.target = '_blank'
            a.click()
            window.URL.revokeObjectURL(res.url)
            a.remove()
        } catch (err) {
            console.log(err)
        }
    }

    const resetJSONFile = () => {
        handleCloseJSONBtn()
        showJsonTileOverlay(true, selectedJson, true)
    }

    const reloadList = async () => {
        setIsListLoading(true)
        await refreshList()
        setIsListLoading(false)
    }

    const handleJsonNameSearch = (e: any) => setSearchKey(e.target.value)

    const handleCloseJsonNameSearch = () => setSearchKey('')

    const handleJSONBtn = (e: React.MouseEvent<HTMLButtonElement>) => setJsonAnchorEl(e.currentTarget)

    useEffect(() => {
        if (Object.keys(jsonClasses).length) {
            setJsonFiles(prevState => {
                return prevState.map(i => {
                    if (i.name in jsonClasses) {
                        i.is_loading = false
                        if (!i.children!.length) {
                            i.children = jsonClasses[i.name].map(x => ({
                                id: uuidv4(),
                                name: x.name,
                                checked: false,
                                children: []
                            }))
                        } else {
                            i.children = i.children!.map(x => ({...x, checked: i.name === jsonClassFilter}))
                        }
                    }
                    return i
                })
            })
        }
    }, [jsonClasses])  // eslint-disable-line

    useEffect(() => {
        setJsonFiles(prevState => {
            if (!prevState.length) return prevState
            
            return prevState.map(i => {
                if (i.name === jsonName) i.checked = true
                else i.checked = false
                i.children = i.children!.map(x => {
                    if (jsonClassFilter === x.name) x.checked = true
                    else x.checked = false
                    return x
                })
                return i
            })
        })
        // Update states using parent data
    }, [jsonName, jsonClassFilter])

    const updateTreeUI = () => {
        try {
            if (!treeRef.current) throw Error('empty ref')

            let newHeight = 18
            for (const parent of jsonFiles) {
                newHeight += rowHeight
                if (parent.children) {
                    if (parent.checked) {
                        newHeight += parent.children!.length * rowHeight
                        treeRef.current.open(parent.id)
                    } else treeRef.current.close(parent.id)
                    for (const child of parent.children) {
                        if (child.checked) treeRef.current.open(child.id)
                        else treeRef.current.close(child.id)
                    }
                }
            }
            if (newHeight > maxHeight) setBoxHeight(maxHeight)
            else setBoxHeight(newHeight)
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        updateTreeUI()
    }, [jsonFiles])  // eslint-disable-line

    const onSelect = (nodes: any[]) => {
        if (nodes.length) {
            try {
                handleAnnoCheckbox('')
                const key = nodes[0].data.name
                if (key.includes('/')) {
                    // JSON Files
                    const filename = key.split('/').pop() || ''
                    if (filename.length < 5) return
                    setSelectedJson(key)
                    if (jsonName === key) {
                        nodes[0].data.is_loading = false
                        if (filename.toLowerCase().includes("heatmap")) renderJsonHeatmap(false, key, '')
                        else showJsonTileOverlay(false, key)
                    } else {
                        if (!(key in jsonClasses)) nodes[0].data.is_loading = true
                        if (key.toLowerCase().includes("heatmap")) renderJsonHeatmap(true, key, '')
                        else showJsonTileOverlay(true, key)
                    }
                } else {
                    // JSON Classes
                    const jsonKey = nodes[0].parent.data.name
                    const filename = jsonKey.split('/').pop() || ''
                    if (filename.length < 5 || !filename.toLowerCase().includes("heatmap")) return
                    setSelectedJson(jsonKey)
                    if (jsonClassFilter === key) renderJsonHeatmap(false, jsonKey, key)
                    else renderJsonHeatmap(true, jsonKey, key)
                }
            } catch (err) {
                console.log(err)
            }
        }
    }

    return (
        <>
            <div className='w-100 mb-3 d-flex justify-content-between align-items-center gap-2'>
                <Paper component="form" sx={{p: '2px 4px', display: 'flex', alignItems: 'center', width: '100%'}}>
                    {!readonly
                        ? <IconButton
                            onClick={handleJSONBtn}
                            id="json-button"
                            aria-controls={jsonMenuOpen ? 'json-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={jsonMenuOpen ? 'true' : undefined}
                        >
                            <MenuIcon />
                        </IconButton>
                        : null
                    }
                    <InputBase
                        sx={{ml: 1, flex: 1}}
                        value={searchKey}
                        onChange={handleJsonNameSearch}
                        placeholder="Search"
                        inputProps={{ 'aria-label': 'search xml' }}
                    />
                    <IconButton
                        onClick={handleCloseJsonNameSearch}
                        type="button"
                        sx={{p: '10px'}}
                        disabled={!searchKey.length}
                        aria-label="reset xml search"
                    >
                        <Close />
                    </IconButton>
                    <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                    <IconButton onClick={reloadList} disabled={isListLoading}  aria-label="reload json list">
                        <Refresh />
                    </IconButton>
                </Paper>
                {!readonly
                    ? <Menu
                        id="json-menu"
                        anchorEl={jsonAnchorEl}
                        open={jsonMenuOpen}
                        onClose={handleCloseJSONBtn}
                        MenuListProps={{ 'aria-labelledby': 'json-button' }}
                    >
                        {isDev && <MenuItem disabled={!selectedJson.length} onClick={downloadJSONFile}>Download</MenuItem>}
                        <MenuItem disabled={!selectedJson.length} onClick={resetJSONFile}>Reset</MenuItem>
                    </Menu>
                    : null
                }
            </div>
            {isListLoading
                ? <Stack spacing={1}>
                    {Array.from({ length: 3 }, (_, i) => i).map(i => (<Skeleton key={i} variant="rectangular" height={rowHeight} />))}
                </Stack>
                : jsonFiles.length
                    ? <Box sx={{ width: '100%', maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
                        <Tree
                            ref={treeRef}
                            data={jsonFiles}
                            openByDefault={false}
                            rowHeight={rowHeight}
                            onSelect={onSelect}
                            height={boxHeight}
                            width='auto'
                            indent={40}
                            disableDrag={true}
                            selection={focusNodeId}
                            searchTerm={searchKey}
                        >
                            {Node}
                        </Tree>
                    </Box>
                    : null
            }
        </>
    )
}

export default JsonList